import { secretsInstance } from './instance';
import type {
  SecretAuditEvent,
  SecretChangeRequestInput,
  SecretPatchRequest,
  SecretRequestItem,
  SecretRequestStatus,
  SecretRequestValue,
  SecretSummary,
  SecretValues,
  SecretsMe,
} from './types';

/** `path` is everything after `skinseoul/`, sent as one encoded segment (`prod%2Fsvc%2Fapi`). */
const secretPath = (path: string) => `/secrets/${encodeURIComponent(path)}`;
const requestPath = (requestId: string) => `/secretRequests/${encodeURIComponent(requestId)}`;

export const fetchSecretsMe = async (): Promise<SecretsMe> =>
  (await secretsInstance.get<SecretsMe>('/secrets/me')).data;

export const fetchSecrets = async (): Promise<SecretSummary[]> =>
  (await secretsInstance.get<SecretSummary[]>('/secrets')).data;

export const createSecret = async (path: string): Promise<{ path: string; name: string }> =>
  (await secretsInstance.post<{ path: string; name: string }>('/secrets', { path })).data;

/** Every call is audited server-side; callers keep the result in component state only, never a query cache. */
export const fetchSecretValues = async (path: string): Promise<SecretValues> =>
  (await secretsInstance.get<SecretValues>(`${secretPath(path)}/values`)).data;

export const patchSecret = async (path: string, body: SecretPatchRequest): Promise<{ versionId: string }> =>
  (await secretsInstance.patch<{ versionId: string }>(secretPath(path), body)).data;

export const createSecretChangeRequest = async (
  path: string,
  body: SecretChangeRequestInput,
): Promise<{ requestId: string }> =>
  (await secretsInstance.post<{ requestId: string }>(`${secretPath(path)}/requests`, body)).data;

export const fetchSecretHistory = async (path: string): Promise<SecretRequestItem[]> =>
  (await secretsInstance.get<SecretRequestItem[]>(`${secretPath(path)}/history`)).data;

export const fetchSecretAudit = async (path: string): Promise<SecretAuditEvent[]> =>
  (await secretsInstance.get<SecretAuditEvent[]>(`${secretPath(path)}/audit`)).data;

export const fetchSecretRequests = async (status: SecretRequestStatus): Promise<SecretRequestItem[]> =>
  (await secretsInstance.get<SecretRequestItem[]>('/secretRequests', { params: { status } })).data;

export const approveSecretRequest = async (requestId: string) =>
  (
    await secretsInstance.post<{ requestId: string; status: string; appliedVersionId: string | null }>(
      `${requestPath(requestId)}/approve`,
    )
  ).data;

export const rejectSecretRequest = async (requestId: string) =>
  (await secretsInstance.post<{ requestId: string; status: string }>(`${requestPath(requestId)}/reject`)).data;

export const fetchSecretRequestValue = async (requestId: string): Promise<SecretRequestValue> =>
  (await secretsInstance.get<SecretRequestValue>(`${requestPath(requestId)}/value`)).data;
